import UpdateConfirmatorForm from "../../components/update/update-confirmator-form";
import { useParams } from "react-router-dom"
import { useEffect, useState } from "react"
import { useAuth } from "../../context/AuthProvider";
import { toast } from "sonner"
import Loading from "../../cors/Loading"
import { BASE_URL } from '../../apiConfig'

export default function Page() {
  const { token } = useAuth();
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [currentUserData, setCurrentUserData] = useState(null)
  const [activeSellers, setActiveSellers] = useState([])

  const fetchData = async () => {
    try {
      const userResponse = await fetch(`${BASE_URL}/api/show-confirmator/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!userResponse.ok) throw new Error('Failed to fetch user data')
      const userData = await userResponse.json()
      setCurrentUserData(userData.data)
      return userData.data
    } catch (error) {
      console.error("Error fetching user data:", error)
      toast.error("Failed to load user data")
      throw error
    }finally{
      setLoading(false);
    }
  }

  const fetchSellersData = async () => {
    try {
      const sellersResponse = await fetch(`${BASE_URL}/api/getactivesaller`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!sellersResponse.ok) throw new Error('Failed to fetch active sellers')
      const sellersData = await sellersResponse.json()
      setActiveSellers(sellersData.data || [])
      return sellersData.data || []
    } catch (error) {
      console.error("Error fetching sellers data:", error)
      toast.error("Failed to load sellers data")
      throw error
    }
  }


  useEffect(() => {
    fetchData()
    fetchSellersData()
  }, [id])

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto min-h-screen py-8 px-4 animate-pulse">
        <div className="mx-auto">
          {/* Header Skeleton */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center space-x-4">
                  {/* Logo Skeleton */}
                  <div className="w-16 h-16 md:w-32 md:h-32 bg-gray-300 rounded-full border-4 border-white shadow-lg"></div>
                  <div className="space-y-2">
                    <div className="h-6 bg-gray-200/50 rounded w-48"></div>
                    <div className="h-4 bg-gray-200/50 rounded w-32"></div>
                    <div className="h-3 bg-gray-200/50 rounded w-40"></div>
                  </div>
                </div>
                <div className="mt-4 md:mt-0">
                  <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                    <div className="h-8 bg-gray-200/50 rounded w-16 mx-auto"></div>
                    <div className="h-3 bg-gray-200/50 rounded w-20 mx-auto mt-2"></div>
                  </div>
                </div>
              </div>
            </div>
  
            {/* Performance Stats Skeleton */}
            <div className="px-6 py-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="text-center">
                    <div className="h-8 bg-gray-200 rounded w-24 mx-auto"></div>
                    <div className="h-4 bg-gray-200 rounded w-28 mx-auto mt-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-20 mx-auto mt-1"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Profile Information Skeleton */}
          <div className="bg-white rounded-2xl p-8 max-xl:p-5 shadow-sm gap-8">
            {/* Edit Button Skeleton */}
            <div className="flex justify-end py-5">
              <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
            </div>
            
            {/* Form Grid Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Personal Information Skeleton */}
              <div className="max-xl:px-4 p-8">
                <div className="flex items-center mb-6">
                  <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-48"></div>
                </div>
  
                <div className="space-y-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i}>
                      <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                      <div className="h-12 bg-gray-100 rounded-2xl"></div>
                    </div>
                  ))}
                  {/* Logo Upload Skeleton */}
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-40 mb-2"></div>
                    <div className="h-12 bg-gray-100 rounded-xl"></div>
                  </div>
                </div>
              </div>
  
              {/* Business Information Skeleton */}
              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-48"></div>
                </div>
  
                <div className="space-y-6">
                  {[...Array(3)].map((_, i) => (
                    <div key={i}>
                      <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                      <div className="h-12 bg-gray-100 rounded-2xl"></div>
                    </div>
                  ))}
                  {/* Bank Select Skeleton */}
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-28 mb-2"></div>
                    <div className="h-12 bg-gray-100 rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          {/* Password Section Skeleton */}
          <div className="bg-white rounded-2xl p-8 max-xl:p-5 shadow-sm gap-8 mt-8">
            <div className="flex items-center mb-6">
              <div className="bg-gray-200 p-3 rounded-lg mr-4"></div>
              <div className="h-6 bg-gray-200 rounded w-40"></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-12 bg-gray-100 rounded-2xl"></div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-8">
              <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
            </div>
          </div>
        </div>
      </div>
      )
  }

  if (!currentUserData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <p>Failed to load user data</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <UpdateConfirmatorForm
          userId={id}
          currentUserData={currentUserData}
          fetchData={fetchData}
          sellersData={activeSellers} 
        />
      </div>
    </div>
  )
}